import React, { useEffect, useState, useMemo } from "react";
import propTypes from "prop-types";
import { css } from "@emotion/react";
import { useLatestActivityOfRepos, useTeam } from "hooks";
import Card from "components/Card";
import { Spinner } from "components/Loader";
import Form, { SIZE, TYPE } from "components/Form";
import { Header } from "components/DisplayBox";
import { H3 } from "components/Text";
import { FormattedMessage } from "react-intl";
import { Banner } from "components/Banner";
import { BANNER_TYPE, CONNECTION_STATES } from "constants/index";
import { formatDate } from "utils";
import { colors, margins } from "style";
import { Col } from "components/Containers";
import GithubSettingsLink from "./GithubSettingsLink";

/**
 * ScopedRepositoriesForm
 *
 * @param {Object} data
 * @param {Object} githubApplicationConnection
 * @param {Boolean} loading
 */
const ScopedRepositoriesForm = ({ data, githubApplicationConnection, loading }) => {
  const organizationId = data?.organization?.id;
  const repositories = githubApplicationConnection?.repositories?.map((repo) => repo.fullName) || [];
  const { updateTeam } = useTeam({});
  const [shouldBeDisabled, setShouldBeDisabled] = useState(false);

  /*
    This code fetches the latest activity of repositories in an optimized way by splitting them into chunks if they exceed 100 entries. 
    It ensures that React hooks are used correctly by calling `useLatestActivityOfRepos` a fixed number of times (up to 3), using `useMemo` to 
    prevent unnecessary recalculations. The fetched data is then transformed into a key-value store based on repository IDs. 
  */
  const splitIntoChunks = (arr, parts) => {
    const chunkSize = Math.ceil(arr.length / parts);
    return Array.from({ length: parts }, (_, i) => arr.slice(i * chunkSize, (i + 1) * chunkSize)).filter(
      (chunk) => chunk.length > 0
    );
  };

  const repoChunks = useMemo(
    () => (repositories.length > 100 ? splitIntoChunks(repositories, 3) : [repositories]),
    [repositories]
  );

  const responses = useMemo(() => {
    return Array(3)
      .fill(null)
      .map((_, index) =>
        useLatestActivityOfRepos({
          organizationId,
          repositories: repoChunks[index] || [],
          skip: !organizationId || (repoChunks[index]?.length ?? 0) === 0,
        })
      );
  }, [organizationId, repoChunks]);

  const reposTimestamp = useMemo(() => responses.flatMap((response) => response.data || []), [responses]);

  const transformedRepos = useMemo(
    () =>
      reposTimestamp.reduce((acc, { fullName, ...rest }) => {
        acc[fullName] = rest;
        return acc;
      }, {}),
    [reposTimestamp]
  );

  useEffect(() => {
    if (githubApplicationConnection?.status === CONNECTION_STATES.suspended) {
      setShouldBeDisabled(true);
    }
  }, [githubApplicationConnection]);

  const handleSubmit = (values) => {
    const gitRepositories = values.gitRepositories
      .filter((repo) => repo.fullName && repo.fullName.trim() !== "")
      .map((repo) => repo.fullName)
      .join(",");

    updateTeam(data.slug, { gitRepositories });
  };

  const formProps = {
    initialValues: {
      gitRepositories: data?.gitRepositories?.split(",").map((repo) => ({ fullName: repo })),
    },
  };

  return (
    <Card>
      <Header>
        <H3>
          <FormattedMessage id="Squads.Profile.ScopedRepositoriesForm.Title" />
        </H3>
      </Header>
      <Col gap="1rem">
        <Banner
          type={BANNER_TYPE.warning}
          isClosable={false}
          cta={() => (
            <GithubSettingsLink
              installationId={githubApplicationConnection?.installationId}
              githubOrganization={githubApplicationConnection?.organizationSlug}
              messageId="Squads.Profile.ActiveSignalBanner.CTA.Title"
            />
          )}
        >
          <FormattedMessage id="Squads.Profile.ScopedRepositoriesForm.Info" />
        </Banner>
        {loading ? (
          <Spinner size={`1.5rem`} />
        ) : (
          <>
            {githubApplicationConnection && (
              <Form
                data={FORM_DATA(githubApplicationConnection, shouldBeDisabled, transformedRepos)}
                onSubmit={(values) => handleSubmit(values)}
                saveButton={{ show: true, disabled: shouldBeDisabled }}
                marginSize={margins.none}
                noPadding
                {...formProps}
              />
            )}
            {!githubApplicationConnection && (
              <Banner type={BANNER_TYPE.warning} isClosable={false}>
                <FormattedMessage id="Squads.Profile.ScopedRepositoriesForm.NoConnection" />
              </Banner>
            )}
          </>
        )}
      </Col>
    </Card>
  );
};

const FORM_DATA = (connection, disabled, transformedRepos) => [
  {
    style: css`
      width: 100%;
    `,
    items: [
      {
        type: TYPE.checkBoxGroup,
        size: SIZE.xlarge,
        disabled: disabled,
        properties: {
          name: "gitRepositories",
          options: connection?.repositories,
          labelFunc: ({ fullName }) => {
            return (
              <>
                <span>{fullName}</span>
                {transformedRepos[fullName]?.latestImportAt && (
                  <span css={styles.checkbox_label_desc}>
                    <FormattedMessage
                      id="Squads.Settings.SignalInstallation.ScopedRepositories.SyncDate"
                      values={{ date: formatDate(transformedRepos[fullName].latestImportAt, "MMM DD, YYYY", true) }}
                    />
                  </span>
                )}
              </>
            );
          },
          valueKey: "fullName",
          showAllOptions: true,
        },
      },
    ],
  },
];

const styles = {
  checkbox_label_desc: css`
    color: ${colors.grayAnatomyLight1};
  `,
};

ScopedRepositoriesForm.propTypes = {
  data: propTypes.object.isRequired,
  githubApplicationConnection: propTypes.object,
  loading: propTypes.bool,
};

export default ScopedRepositoriesForm;
